import { BusinessSeatsType } from '@api';
import { FormActions } from '@common';
import { zodResolver } from '@hookform/resolvers/zod';
import { NumberField, TextareaField } from '@hypeit-gmbh/hypeit-ui';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  BusinessSeatsFormDataType,
  useUpdateVenueBusinessSeats,
  useVenueBusinessSeatsFormSchema,
} from './hooks';

const TRANS_PATH = 'VENUE_BUSINESS_SEATS.FORM';

type VenueBusinessSeatsFormPropsType = {
  venueId: string;
  overviewVersion: string;
  venueBusinessSeats: BusinessSeatsType;
  onClose: () => void;
};

export const VenueBusinessSeatsForm: React.FC<
  VenueBusinessSeatsFormPropsType
> = ({ venueId, overviewVersion, venueBusinessSeats, onClose }) => {
  const { t } = useTranslation('venueDetailsOverview');

  const { schema, defaultValues } =
    useVenueBusinessSeatsFormSchema(venueBusinessSeats);
  const { updateVenueBusinessSeats } = useUpdateVenueBusinessSeats({
    venueId,
    overviewVersion,
    onClose,
  });

  const methods = useForm<BusinessSeatsFormDataType>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const handleSubmit = async (data: BusinessSeatsFormDataType) => {
    await updateVenueBusinessSeats(data);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <Stack>
          <NumberField<BusinessSeatsFormDataType>
            fieldName="maxSeats"
            label={t(`${TRANS_PATH}.MAX_SEATS_LABEL`)}
            allowDecimal={false}
          />
          <NumberField<BusinessSeatsFormDataType>
            fieldName="uplift"
            label={t(`${TRANS_PATH}.UPLIFT_LABEL`)}
            suffix="€"
          />
          <NumberField<BusinessSeatsFormDataType>
            fieldName="share"
            label={t(`${TRANS_PATH}.SHARE_LABEL`)}
            suffix="€"
          />
          <TextareaField<BusinessSeatsFormDataType>
            fieldName="notes"
            label={t(`${TRANS_PATH}.NOTES_LABEL`)}
          />
          <FormActions
            onCancel={onClose}
            saveButtonText={t('SAVE_BUTTON', { ns: 'common' })}
            cancelButtonText={t('CANCEL_BUTTON', { ns: 'common' })}
          />
        </Stack>
      </form>
    </FormProvider>
  );
};
